import React, { useCallback } from 'react';
import { LoadingButton } from '@atlaskit/button';
import { css } from '@emotion/core';
import { useMutation } from '@tanstack/react-query';
import { subDays } from 'date-fns';

import { apis, apiResolve } from '@/services';
import { useFetchErrorController } from '@/controllers';
import { SPACE_COMPACT } from '@/styles';
import { PerformanceWithEvents } from '@/ui';
import { COMPLIANCE_PERIOD_IN_DAYS } from '@/constants';

//  Rerun collection state and labels
enum RerunState {
  Ready = 'Rerun collection',
  Completed = 'Rerun requested',
  Error = 'Failed to rerun',
}

export const RerunCollectionButton: React.FC<{
  publicId: string;
  shouldDeleteOldDatapoints?: boolean;
  performance?: PerformanceWithEvents;
}> = ({ publicId, shouldDeleteOldDatapoints, performance }) => {
  const [state, setState] = React.useState<RerunState>(RerunState.Ready);
  const { onError } = useFetchErrorController();
  const mutation = useMutation(
    () =>
      apiResolve(
        shouldDeleteOldDatapoints
          ? apis.collection.deleteSloDataPointsFrom1({
              publicId,
              start:
                performance?.latestDataPointTimestamp ||
                // times 2 to fetch hidden data out of 28 days compliance window
                subDays(new Date(), COMPLIANCE_PERIOD_IN_DAYS * 2),
            })
          : apis.collection.collectSlo({ publicId }),
        false
      ),
    {
      onError,
    }
  );

  const onRerunCollection = useCallback(async () => {
    if (state === RerunState.Ready) {
      mutation.mutate();
    }
  }, [mutation, state]);

  React.useEffect(() => {
    if (mutation.isSuccess) {
      setState(RerunState.Completed);
    } else if (mutation.isError) {
      setState(RerunState.Error);
    }
  }, [mutation.isSuccess, mutation.isError]);

  return (
    <LoadingButton
      css={[
        css`
          margin: ${SPACE_COMPACT}px;
        `,
      ]}
      isLoading={mutation.isLoading}
      isDisabled={state !== RerunState.Ready}
      onClick={onRerunCollection}
      testId="rerun-collection-button"
    >
      {state.toString()}
    </LoadingButton>
  );
};
